import { useLocalStorageState } from "@outschool/local-storage";
import { dayjs } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import {
  useIsEligibleFor40PercentCouponUpsell,
  useRequiresMembershipForEnrollment
} from "@outschool/ui-components-website";
import React from "react";

// Custom event to sync localStorage changes within the same tab
const CDP_MODAL_STORAGE_EVENT = "cdp-modal-storage-change";

export default function useCDPMembershipModal() {
  const {
    requiresMembershipForEnrollment,
    requiresEsaMembershipForEnrollment
  } = useRequiresMembershipForEnrollment();
  const { isEligibleFor40PercentCouponUpsell } =
    useIsEligibleFor40PercentCouponUpsell();
  const { currentUser } = useSession();
  const [cdpMembershipModalDismissedAt, setCdpMembershipModalDismissedAt] =
    useLocalStorageState<string | null>("CDPMembershipModalDismissedAt", null);

  // Listen for storage changes to sync across hook instances
  React.useEffect(() => {
    const handleCustomStorageEvent = (e: CustomEvent) => {
      if (e.detail.key === "CDPMembershipModalDismissedAt") {
        setCdpMembershipModalDismissedAt(e.detail.value);
      }
    };

    // Only listen for same-tab changes
    window.addEventListener(
      CDP_MODAL_STORAGE_EVENT,
      handleCustomStorageEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        CDP_MODAL_STORAGE_EVENT,
        handleCustomStorageEvent as EventListener
      );
    };
  }, [setCdpMembershipModalDismissedAt]);

  const isMembershipNeeded =
    (requiresMembershipForEnrollment &&
      (!currentUser?.hasSubscription ||
        !currentUser?.canPayWithSubscriptionCredits) &&
      !currentUser?.hasEsaMembership) ||
    requiresEsaMembershipForEnrollment;

  const isSnoozed =
    cdpMembershipModalDismissedAt &&
    dayjs(cdpMembershipModalDismissedAt).isAfter(dayjs().subtract(24, "hours"));

  // show the modal when:
  // 1) membership is required, OR
  // 2) a la carte user is being offered the coupon AND they have not snoozed the modal
  const shouldShowCDPMembershipModal =
    isMembershipNeeded || (isEligibleFor40PercentCouponUpsell && !isSnoozed);

  const snoozeCDPMembershipModal = React.useCallback(() => {
    const timestamp = new Date().toISOString();
    setCdpMembershipModalDismissedAt(timestamp);

    // Dispatch custom event to notify other hook instances in the same tab
    window.dispatchEvent(
      new CustomEvent(CDP_MODAL_STORAGE_EVENT, {
        detail: { key: "CDPMembershipModalDismissedAt", value: timestamp }
      })
    );
  }, [setCdpMembershipModalDismissedAt]);

  return {
    snoozeCDPMembershipModal,
    shouldShowCDPMembershipModal
  };
}
